import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import styles from './style/Sidebar.module.scss';
import logo from '../../assets/images/Logo-bodhiwell.png';

import { MenuItem } from '../../components/Sidebar/MenuItem';
import { useSidebar } from './hooks/useSidebar';
import { ROUTES_TO_BLOCK } from '../../config/navs/mainMenuItems';

const Sidebar = React.forwardRef<HTMLElement>((_props, ref) => {
  const location = useLocation();
  const {
    t,
    user,
    isSidebarOpen,
    toggleSidebar,
    isMobile,
    submenuData,
    menuItems,
    menuGroups,
    statusLoading,
    completionLoading,
    userStatusData,
    shouldBlockMenuItems,
    isProfileComplete,
    isPersonalInfoComplete,
    handleItemClick,
    handleLogout
  } = useSidebar();

  if (isMobile && submenuData.items) {
    return null;
  }

  return (
    <aside
      ref={ref}
      className={classNames(styles['sidebar'], {
        [styles['sidebar-closed']]: !isSidebarOpen
      })}
    >
      <Link to="/" className={styles['sidebar__brand']}>
        {isSidebarOpen ? (
          <img src={logo} alt="Bodhiwell" className={styles['sidebar__logo-img']} />
        ) : (
          <div className={styles['sidebar__brand-mark']} style={{ backgroundImage: `url(${logo})` }} />
        )}
      </Link>

      <div className={styles['sidebar__menu']}>
        {menuGroups.map((group) => {
          const itemsInGroup = menuItems.filter(item => item.group === group.id);
          if (itemsInGroup.length === 0) return null;

          const isOtherGroup = group.id === 'other';
          const isGroupActive = itemsInGroup.some(item => {
            const itemUrl = item.path ? new URL(item.path, 'http://localhost').pathname : '';
            if (itemUrl === '/blog' && location.pathname.startsWith('/blog')) return true;
            if (itemUrl === '/availability' && location.pathname.startsWith('/availability')) return true;
            if (itemUrl === '/reservations' && location.pathname.startsWith('/reservations')) return true;
            if (itemUrl === '/my-consultation' && location.pathname.startsWith('/my-consultation')) return true;
            return location.pathname === itemUrl;
          });

          return (
            <React.Fragment key={group.id}>
              {isOtherGroup ? (
                <div className={styles['sidebar__notifications']}>
                  <nav className={styles['sidebar__nav']}>
                    {itemsInGroup.map((item) => {
                      const isDisabled = shouldBlockMenuItems && ROUTES_TO_BLOCK.includes(item.labelKey);
                      return (
                        <MenuItem
                          key={item.labelKey}
                          item={item}
                          isSidebarOpen={isSidebarOpen}
                          onClick={handleItemClick}
                          isDisabled={isDisabled || statusLoading || completionLoading}
                          showWarning={false}
                        />
                      );
                    })}
                  </nav>
                </div>
              ) : (
                <details className={classNames(styles['sidebar__accordion'], {
                  [styles['sidebar__accordion--consultant']]: group.id === 'consultante'
                })} open={group.id === 'registro' || isGroupActive}>
                  <summary className={styles['sidebar__accordion-summary']}>
                    {isSidebarOpen && <span className={styles['sidebar__accordion-label']}>{group.label}</span>}
                    {isSidebarOpen && <span aria-hidden="true" className={styles['sidebar__accordion-symbol']}></span>}
                  </summary>
                  <nav className={styles['sidebar__nav']}>
                    {itemsInGroup.map((item) => {
                      const isDisabled = shouldBlockMenuItems && ROUTES_TO_BLOCK.includes(item.labelKey);

                      let showWarning = false;
                      if (user?.type_account === 'professional' && userStatusData?.status !== 'active') {
                        if (item.labelKey === 'appAdmin.dashboard.profilePublic' || item.labelKey === 'appAdmin.dashboard.profile') {
                          showWarning = !isProfileComplete;
                        } else if (item.labelKey === 'appAdmin.dashboard.profileMenu.personalInfo') {
                          showWarning = !isPersonalInfoComplete;
                        }
                      }

                      return (
                        <MenuItem
                          key={item.labelKey}
                          item={item}
                          isSidebarOpen={isSidebarOpen}
                          onClick={handleItemClick}
                          isDisabled={isDisabled || statusLoading || completionLoading}
                          showWarning={showWarning}
                        />
                      );
                    })}
                  </nav>
                </details>
              )}

              {!isOtherGroup && <div className={styles['sidebar__divider']} />}
            </React.Fragment>
          );
        })}
      </div>



      <div className={styles['sidebar__toggle-container']} onClick={toggleSidebar}>
        <div className={classNames(
          styles['sidebar__toggle-btn'],
          isSidebarOpen ? styles['is-open'] : styles['is-closed']
        )}>
          <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
        </div>
      </div>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;